<script setup lang="ts" generic="Row extends object, Params extends object">
import { computed, inject, watch } from "vue";
import { useGeneratedTable } from "@/composables/generated/useGeneratedTable";
import type { GeneratedTableConfig } from "@/composables/generated/contracts";
import type { HeaderType } from "@/composables/useTableHelper";
import type { DirtyFlag } from "@/composables/useDirtyFlag";
import TableFilterControl from "@/components/shared/table/TableFilterControl.vue";
import ReportingTableServer from "@/components/shared/table/ReportingTableServer.vue";

const props = defineProps<{
  config: GeneratedTableConfig<Row, Params>;
  filterKeys?: Extract<keyof Params, string>[];
  headers?: HeaderType<Row>[];
  refreshFlag?: string;
  refreshKey?: number;
  hideExport?: boolean;
}>();
const { table, error } = useGeneratedTable(props.config);
if (props.headers) {
  table.extraHeaders.value = props.headers;
  table.resetVisibleHeaders();
}
const filterMeta = computed(() =>
  Object.fromEntries(
    props.config.searchMeta.properties
      .filter((property) =>
        props.filterKeys?.some((key) => key === property.propertyName),
      )
      .map((property) => [property.propertyName, property]),
  ),
);
const flag = props.refreshFlag
  ? inject<DirtyFlag | undefined>(props.refreshFlag, undefined)
  : undefined;
if (flag)
  watch(
    () => flag.isDirty,
    async (dirty) => {
      if (dirty) {
        flag.update(false);
        await table.reloadItems();
      }
    },
  );
watch(
  () => props.refreshKey,
  () => table.reloadItems(),
);
defineExpose({ table, reload: table.reloadItems });
</script>

<template>
  <TableFilterControl
    :table="table"
    :filter-meta="filterMeta"
    :hide-export="hideExport"
  />
  <v-alert v-if="error" type="error" role="alert" class="mb-4">
    {{ error }} Previous results may be shown.
  </v-alert>
  <ReportingTableServer :table="table">
    <template v-for="(_, name) in $slots" v-slot:[name]="scope">
      <slot :name="name" v-bind="scope || {}" />
    </template>
  </ReportingTableServer>
</template>
